/**
 * Phase 18.17 Batch 5 — Generic extra-page PageBanner wrapper.
 *
 * One thin React component per slug-config. Each call site provides:
 *   - slug (matches the backend extra-pages factory)
 *   - eyebrowIcon (a lucide-react component)
 *   - optional fallback overrides
 *   - optional testid (default = `${slug}-banner`)
 *
 * Rendering itself is delegated to the shared ToolPageBanner so all
 * Batch 2/3/4/5 banners look and behave identically (USP-badge ribbon,
 * banner CTAs, mobile spacing fix from Feb 2026).
 */
import React from "react";
import {
  Map as MapIcon, Info, Truck, Calculator, ShieldCheck, Building2,
  HelpCircle, Ship,
} from "lucide-react";
import useExtraPageCms from "../hooks/useExtraPageCms";
import { ToolPageBanner } from "./ToolPageCms";

const SLUG_CONFIG = {
  hubs:                 { eyebrowIcon: MapIcon },
  about:                { eyebrowIcon: Building2 },
  faq:                  { eyebrowIcon: HelpCircle },
  "pickup-delivery":    { eyebrowIcon: Truck },
  "pickup-calculator":  { eyebrowIcon: Calculator },
  "shipping-to-curacao":{ eyebrowIcon: Ship },
};

const FALLBACKS = {
  hubs: {
    eyebrow: "Vestigingen",
    title: "Onze 5999Cargo hubs",
    highlight: "wereldwijd",
    subtitle: "Een lokaal aanspreekpunt op de plekken waar uw zending vertrekt of aankomt.",
    badges: [
      { icon: "map",     label: "Caribbean + Europa + USA" },
      { icon: "support", label: "Lokaal aanspreekpunt" },
      { icon: "shield",  label: "Vertrouwde partners" },
    ],
  },
  about: {
    eyebrow: "Over 5999Cargo",
    title: "Cargo with",
    highlight: "a smile",
    subtitle: "Wij verbinden Nederland met het Caribisch gebied via een betrouwbare zee- en luchtroute.",
    badges: [
      { icon: "ship",    label: "Sinds jaren actief" },
      { icon: "support", label: "Persoonlijke aanpak" },
      { icon: "shield",  label: "Documenten op orde" },
    ],
  },
  faq: {
    eyebrow: "Veelgestelde vragen",
    title: "Antwoorden op",
    highlight: "de meest gestelde vragen",
    subtitle: "Documenten, gevaarlijke goederen, betalen, transittijden — vind hieronder snel het antwoord.",
    badges: [
      { icon: "support", label: "Snel antwoord per categorie" },
      { icon: "shield",  label: "Up-to-date" },
      { icon: "info",    label: "Per onderwerp gerangschikt" },
    ],
  },
  "pickup-delivery": {
    eyebrow: "Pickup & Delivery",
    title: "Wij halen op,",
    highlight: "wij leveren af",
    subtitle: "Pickup-service binnen Nederland en delivery vanaf onze Caribische hubs.",
    badges: [
      { icon: "truck",  label: "Pickup heel NL" },
      { icon: "map",    label: "Delivery Caribbean" },
      { icon: "shield", label: "Tracking onderweg" },
    ],
  },
  "pickup-calculator": {
    eyebrow: "Pickup Calculator",
    title: "Bereken uw",
    highlight: "pickup-prijs",
    subtitle: "Vul postcode, gewicht en afmetingen in — wij berekenen direct een indicatie.",
    badges: [
      { icon: "truck",   label: "Indicatie pickup-prijs" },
      { icon: "info",    label: "Whatever is greater" },
      { icon: "support", label: "Vragen? Bel direct" },
    ],
  },
  "shipping-to-curacao": {
    eyebrow: "Verzenden naar Curaçao",
    title: "De snelste route van",
    highlight: "Nederland naar Curaçao",
    subtitle: "Zee- of luchtvracht, pakketten of containers, particulier of zakelijk.",
    badges: [
      { icon: "ship",    label: "Zee- en luchtvracht" },
      { icon: "package", label: "Pakket of container" },
      { icon: "support", label: "Persoonlijke begeleiding" },
    ],
  },
};

export default function ExtraPageBanner({ slug, testid, extras }) {
  const cfg = SLUG_CONFIG[slug] || {};
  const fallback = { page_banner: FALLBACKS[slug] || {}, intro: {}, cta_band: {} };
  const { data } = useExtraPageCms(slug, fallback);
  const banner = data?.page_banner || fallback.page_banner;
  return (
    <ToolPageBanner
      banner={banner}
      eyebrowIcon={cfg.eyebrowIcon || Info}
      testid={testid || `${slug}-banner`}
      extras={extras}
    />
  );
}
